import { type FC, useMemo, useState } from 'react'
import { JsonLd } from 'react-schemaorg'
import { type VideoObject } from 'schema-dts'

import { useExperimentalAddStaticFileHost } from '../../../experiments/new-cdn-experiment/useExperimentalAddStaticFileHost'
import {
  IMAGE_PRODUCT_FALLBACK_SRC,
  IMAGE_PRODUCT_PRESCRIPTION_FALLBACK_SRC,
} from '../../../image'
import { getVideoSchema } from '../../seo/schemas/getVideoSchema'

import { ProductCarouselGalleryWithThumbnails } from './ProductCarouselGalleryWithThumbnails'
import { ProductCarouselLargeGalleryModal } from './ProductCarouselLargeGalleryModal'
import { type ProductCarouselProps } from './ProductCarouselProps'

const ProductCarousel: FC<ProductCarouselProps> = ({
  images: imagesProp,
  isPrescription,
  productName,
  videos: videosProp,
}) => {
  const images = useMemo(() => imagesProp ?? [], [imagesProp])
  const videos = useMemo(() => videosProp ?? [], [videosProp])

  const { addStaticCdnHostToUrl } = useExperimentalAddStaticFileHost()

  const fallbackImageUrl = isPrescription
    ? addStaticCdnHostToUrl(IMAGE_PRODUCT_PRESCRIPTION_FALLBACK_SRC)
    : addStaticCdnHostToUrl(IMAGE_PRODUCT_FALLBACK_SRC)
  const imagesToShow = images.length > 0 ? images : [fallbackImageUrl]
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [showLargeGalleryModal, setShowLargeGalleryModal] = useState(false)
  const [
    forceLargeGalleryToJumpToFirstVideo,
    setForceLargeGalleryToJumpToFirstVideo,
  ] = useState(false)
  const [playVideoOnScroll, setPlayVideoOnScroll] = useState(false)

  const handleOnModalClose = (): void => {
    setShowLargeGalleryModal(false)
    setForceLargeGalleryToJumpToFirstVideo(false)
  }

  const handleIndexChanged = (index: number): void => {
    setCurrentImageIndex(index)
  }

  const handleVideoBadgeClick = (scrollToVideo?: boolean): void => {
    if (scrollToVideo) {
      setPlayVideoOnScroll(true)
    } else {
      setForceLargeGalleryToJumpToFirstVideo(true)
      setShowLargeGalleryModal(true)
    }
  }

  return (
    <div className="relative flex flex-col items-center p-7 pb-3.5 tablet:px-20">
      <ProductCarouselGalleryWithThumbnails
        images={imagesToShow}
        initialImageIndex={0}
        onIndexChanged={handleIndexChanged}
        onItemClick={(): void => {
          setShowLargeGalleryModal(true)
        }}
        onVideoBadgeClicked={handleVideoBadgeClick}
        playVideoOnScrolledIntoView={playVideoOnScroll}
        productName={productName}
        showSimilarImagesNotice
        showVideoBadgeIfVideoAvailable
        thumbnails="all"
        videos={videos}
        zoomEnabled
      />
      {videos.map((video) => (
        <JsonLd<VideoObject>
          item={getVideoSchema(productName, video)}
          key={video.code}
        />
      ))}
      <ProductCarouselLargeGalleryModal
        images={imagesToShow}
        initialIndex={
          forceLargeGalleryToJumpToFirstVideo && videos.length > 0
            ? imagesToShow.length
            : currentImageIndex
        }
        isOpen={showLargeGalleryModal}
        onClose={handleOnModalClose}
        playVideoOnScrolledIntoView={
          forceLargeGalleryToJumpToFirstVideo ||
          currentImageIndex >= imagesToShow.length
        }
        productName={productName}
        videos={videos}
      />
    </div>
  )
}

export { ProductCarousel }
